<template>
  <div class="wechat-subscribe-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('wechatSubscribe.page.title')" :subtitle="$t('wechatSubscribe.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-refresh" @click="_openOpenSyncDataModel()">
          {{ $t('wechatSubscribe.syncUser') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_queryWechatSubscribeMethod()">
        <template #left>
          <span class="ds-panel__title">{{ $t('wechatSubscribe.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="wechatSubscribeInfo.users" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('wechatSubscribe.page.empty')" @retry="_queryWechatSubscribeMethod()">
        <el-table-column prop="openType" :label="$t('wechatSubscribe.type')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveOpenType(scope.row)" :label="resolveOpenTypeLabel(scope.row)" />
          </template>
        </el-table-column>
        <el-table-column prop="openId" :label="$t('wechatSubscribe.openId')" align="center" min-width="220">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.openId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="unionId" :label="$t('wechatSubscribe.unionId')" align="center" min-width="220">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.unionId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="appId" label="app_id" align="center" width="200">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.appId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('wechatSubscribe.syncTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('wechatSubscribe.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <sync-subscribe-wechat ref="syncSubscribeWechat" @success="handleSyncSuccess"></sync-subscribe-wechat>
  </div>
</template>

<script>
import { listWechatSubscribe } from '@/api/community/wechatSubscribeApi'
import { getCommunityId } from '@/api/community/communityApi'
import SyncSubscribeWechat from '@/components/community/syncSubscribeWechat'
import { formatDateTimeSec } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'WechatSubscribeList',
  components: {
    SyncSubscribeWechat,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      wechatSubscribeInfo: {
        users: [],
        conditions: {
          name: '',
          appId: '',
          weChatType: '1000'
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this._listWechatSubscribes(this.page.current, this.page.size)
  },
  methods: {
    async _listWechatSubscribes(page, size) {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page,
          row: size,
          communityId: getCommunityId(),
          ...this.wechatSubscribeInfo.conditions
        }
        const { data, total } = await listWechatSubscribe(params)
        this.wechatSubscribeInfo.users = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('获取关注用户失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('wechatSubscribe.page.loadFailed')
        this.$message.error(this.$t('wechatSubscribe.fetchError'))
      } finally {
        this.loading = false
      }
    },
    _openOpenSyncDataModel() {
      this.$refs.syncSubscribeWechat.open()
    },
    _queryWechatSubscribeMethod() {
      this.page.current = 1
      this._listWechatSubscribes(this.page.current, this.page.size)
    },
    handleSyncSuccess() {
      this._listWechatSubscribes(this.page.current, this.page.size)
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this._listWechatSubscribes(this.page.current, val)
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listWechatSubscribes(val, this.page.size)
    },
    /**
     * 同步时间格式化（后端返回 "yyyy-MM-dd HH:mm:ss" 字符串）
     */
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    /**
     * 关注来源语义类型：WECHAT 为微信来源，其余按其它来源处理。
     */
    resolveOpenType(row) {
      if (!row) return 'muted'
      return row.openType === 'WECHAT' ? 'primary' : 'muted'
    },
    /**
     * 关注来源文案
     */
    resolveOpenTypeLabel(row) {
      if (!row) return '-'
      return row.openType === 'WECHAT' ? this.$t('wechatSubscribe.wechat') : this.$t('wechatSubscribe.other')
    }
  }
}
</script>

<style lang="scss" scoped>
.wechat-subscribe-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}
</style>
